/**
 * mapPreviewsYMap.js
 *
 * Yjs CRUD for map embed preview snapshots stored in Y.Map("mapPreviews").
 * Each map node's preview is keyed by nodeId.
 *
 * Structure inside the Y.Doc:
 *
 *   Y.Map("mapPreviews")
 *     └── [nodeId]: Y.Map
 *           ├── preview: string    ← uploaded image URL (or data URL fallback)
 *           └── signature: string  ← content/geometry signature the preview was captured from
 *                                     (MapPreviewQueueService's computeSignature output; see
 *                                     `signature` field docs on setMapPreview below)
 *
 * Mirrors the shape of annotationsYMap.js so both overlays share the same pattern.
 */

import * as Y from 'yjs';

// ---------------------------------------------------------------------------
// Entry helpers
// ---------------------------------------------------------------------------

/**
 * Get or create the Y.Map entry for a given nodeId.
 * Must be called inside a ydoc.transact() block when creating.
 *
 * @param {import('yjs').Map} yMapPreviews  Root Y.Map("mapPreviews")
 * @param {string}            nodeId
 * @returns {import('yjs').Map}
 */
export function getMapPreviewEntry(yMapPreviews, nodeId) {
    if (!yMapPreviews || !nodeId) return null;
    if (!yMapPreviews.has(nodeId)) {
        const entry = new Y.Map();
        entry.set('preview', '');
        yMapPreviews.set(nodeId, entry);
    }
    return yMapPreviews.get(nodeId);
}

// ---------------------------------------------------------------------------
// Write API
// ---------------------------------------------------------------------------

/**
 * Persist the preview image URL (or data URL fallback) for a given nodeId.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  yMapPreviews
 * @param {string}             nodeId
 * @param {string}             urlOrDataURL  Uploaded image URL (preferred) or PNG/WebP data URL
 * @param {string}             [signature]   MINSKY-17028 follow-up: the render-input signature
 *   (MapPreviewQueueService's `computeSignature()` output) this capture was produced from. Stored
 *   alongside `preview` so a *later, independent* MapPreviewQueueService instance (e.g. after an
 *   editor remount / WS reconnect, which always starts with an empty in-memory `_lastSignature`
 *   cache) can tell "nothing about this map's content actually changed since the last successful
 *   capture" WITHOUT re-rendering first — comparing the freshly *rendered* image byte-for-byte
 *   isn't reliable for that: OpenLayers' WebGL/canvas rendering is not guaranteed pixel-identical
 *   across separate map instances (anti-aliasing/label-placement/tile-load-order noise) even for
 *   unchanged input geometry, so an input-signature comparison is used instead. Omit this
 *   parameter to leave a previously-stored signature untouched (used by callers that persist an
 *   externally-sourced URL rather than a signature-tracked capture).
 */
export function setMapPreview(ydoc, yMapPreviews, nodeId, urlOrDataURL, signature) {
    if (!ydoc || !yMapPreviews || !nodeId) return;
    ydoc.transact(() => {
        const entry = getMapPreviewEntry(yMapPreviews, nodeId);
        if (!entry) return;
        entry.set('preview', urlOrDataURL ?? '');
        if (signature !== undefined) entry.set('signature', signature ?? '');
    }, 'map-preview');
}

// ---------------------------------------------------------------------------
// Read API
// ---------------------------------------------------------------------------

/**
 * Read the preview image URL (or fallback data URL) for a nodeId.
 * Returns an empty string if no preview has been stored yet.
 *
 * @param {import('yjs').Map} yMapPreviews
 * @param {string}            nodeId
 * @returns {string}
 */
export function getMapPreview(yMapPreviews, nodeId) {
    if (!yMapPreviews || !nodeId) return '';
    const entry = yMapPreviews.get(nodeId);
    return entry ? (entry.get('preview') || '') : '';
}

/**
 * Read the render-input signature the currently-persisted preview was captured from, or ''
 * if none has been recorded yet (pre-existing entries from before this field existed, or a
 * preview set via an external-URL path that didn't pass a signature).
 *
 * @param {import('yjs').Map} yMapPreviews
 * @param {string}            nodeId
 * @returns {string}
 */
export function getMapPreviewSignature(yMapPreviews, nodeId) {
    if (!yMapPreviews || !nodeId) return '';
    const entry = yMapPreviews.get(nodeId);
    return entry ? (entry.get('signature') || '') : '';
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to preview changes for a specific nodeId.
 * Fires whenever the 'preview' key is updated for this map node.
 *
 * @param {import('yjs').Map} yMapPreviews
 * @param {string}            nodeId
 * @param {Function}          callback  () => void
 * @returns {Function}  unsubscribe function
 */
export function observeMapPreview(yMapPreviews, nodeId, callback) {
    if (!yMapPreviews || !nodeId || typeof callback !== 'function') return () => {};

    const handler = (events) => {
        const relevant = events.some((e) =>
            (e.path.length > 0 && e.path[0] === nodeId) ||
            (e.path.length === 0 && e.keysChanged?.has(nodeId))
        );
        if (relevant) callback(events);
    };

    yMapPreviews.observeDeep(handler);
    return () => yMapPreviews.unobserveDeep(handler);
}
